import { useState } from 'react';
import { toast } from 'sonner';
import { ApiError, spacesApi } from '@/lib/api';
import { useSpaceWrite } from '@/hooks/queries/useSpaceWrite';
import type { SpaceExpenseRow } from '@/lib/spaces/expenses';
import { track } from '@/lib/telemetry';

const SPACE_READS = ['space-expenses-page', 'space-expenses', 'space-ledger', 'space-home', 'space-audit', 'space-commitments', 'space-budget'];
// Personal reads that list or count this person's sent expenses and exclusions across spaces.
const PERSONAL_READS = ['space-expense-index', 'space-rules-overview', 'space-send-rules'];

export function useSpaceExpenseRemoval(spaceId: string, onRemoved: () => void) {
  const { write, mutate, userId } = useSpaceWrite();
  const [busy, setBusy] = useState(false);
  const remove = async (expense: SpaceExpenseRow, exception: boolean) => {
    setBusy(true);
    try {
      await write((token) => spacesApi.removeExpense(token, spaceId, expense.id, exception));
    } catch (error) {
      toast.error(removalError(error));
      return;
    } finally { setBusy(false); }
    track('space.expense.removed', { exception });
    toast.success(exception ? `${expense.name} saiu do espaço. Esse estabelecimento não entra mais sozinho.` : `${expense.name} saiu do espaço.`);
    // Offset pages are a live feed: every period, filter and balance of this space is stale after a removal.
    void mutate((key) => Array.isArray(key) && key[1] === userId
      && (PERSONAL_READS.includes(key[0]) || (key[2] === spaceId && SPACE_READS.includes(key[0]))));
    onRemoved();
  };
  return { busy, remove };
}

function removalError(error: unknown): string {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 404) return 'Esta despesa não está mais no espaço. Atualize a página.';
  if (status === 403 || status === 409) return 'Não é possível tirar esta despesa agora. Confira seu acesso e se o espaço está aberto.';
  return 'Não foi possível tirar a despesa. Tente novamente.';
}
